'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useRef } from 'react';

import { Prize, PrizeBodyVariant } from '@/lib/prize-wheel/prizes';
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/shadcn/ui/dialog';

export interface PrizeResultCopy {
  eyebrow?: string;
  topPrizeEyebrow?: string;
  bodyChallenge?: string;
  bodyFunded?: string;
  bodyVoucher?: string;
  ctaLabel?: string;
}

const DEFAULTS: Required<PrizeResultCopy> = {
  eyebrow: 'You won',
  topPrizeEyebrow: 'Top Prize',
  bodyChallenge:
    'Your Challenge account is being set up. Check your inbox to start trading.',
  bodyFunded:
    'Your live Funded account is being provisioned. Check your inbox for setup details.',
  bodyVoucher: 'Your discount is ready to use on your next qualifying order.',
  ctaLabel: 'Continue',
};

function bodyFor(variant: PrizeBodyVariant, copy: Required<PrizeResultCopy>) {
  if (variant === 'funded') return copy.bodyFunded;
  if (variant === 'voucher') return copy.bodyVoucher;
  return copy.bodyChallenge;
}

export interface PrizeResultDialogProps {
  prize: Prize | null;
  onClose: () => void;
  copy?: PrizeResultCopy;
}

export default function PrizeResultDialog({
  prize,
  onClose,
  copy,
}: PrizeResultDialogProps) {
  const continueRef = useRef<HTMLButtonElement>(null);

  const text = { ...DEFAULTS } as Required<PrizeResultCopy>;
  for (const [key, value] of Object.entries(copy ?? {})) {
    if (value) text[key as keyof PrizeResultCopy] = value;
  }

  return (
    <Dialog open={!!prize} onOpenChange={(open) => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay className="bg-black/30 backdrop-blur-none" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => {
            // Land on the one action, without scrolling the page under the scrim.
            e.preventDefault();
            continueRef.current?.focus({ preventScroll: true });
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            (e.target as HTMLElement | null)?.focus?.({ preventScroll: true });
          }}
          className="fixed left-[50%] top-[50%] z-[999] translate-x-[-50%] translate-y-[-50%] rounded-[0.75rem] border border-white/40 bg-white/10 p-[0.75rem] outline-0 backdrop-blur-[4rem] duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] max-lg:w-[min(49.25rem,calc(100%-4rem))] lg:w-[57.75rem]"
        >
          <VisuallyHidden>
            <DialogTitle>
              {text.eyebrow} {prize?.modalAmount} {prize?.modalQualifier}
            </DialogTitle>
          </VisuallyHidden>

          <div className="flex flex-col items-center gap-[3rem] overflow-hidden rounded-[0.5rem] bg-white p-[4rem] font-geist">
            <div className="flex w-full flex-col items-center gap-[2rem]">
              <p className="mb-0 font-blacker text-[2rem] italic leading-[1.5] text-black">
                {prize?.isTopPrize ? text.topPrizeEyebrow : text.eyebrow}
              </p>

              <p className="mb-0 w-[min(41.375rem,100%)] text-center font-semibold leading-[1.02] text-black max-lg:text-[4.5rem] max-lg:tracking-[-0.225rem] lg:text-[5rem] lg:tracking-[-0.25rem]">
                <span
                  className="font-blacker font-normal italic text-[#003828]"
                  style={{
                    background:
                      'linear-gradient(to top, #DFEDE2 48%, transparent 48%)',
                    WebkitBoxDecorationBreak: 'clone',
                    boxDecorationBreak: 'clone',
                  }}
                >
                  {prize?.modalAmount}
                </span>
                <br />
                {prize?.modalQualifier}
              </p>

              <p className="mb-0 w-full text-center text-[2.25rem] font-normal leading-[1.5] text-black opacity-70 lg:max-w-[41.375rem]">
                {prize ? bodyFor(prize.modalBodyVariant, text) : ''}
              </p>
            </div>

            <button
              ref={continueRef}
              type="button"
              onClick={onClose}
              className="flex w-[min(29.75rem,100%)] items-center justify-center rounded-[0.25rem] bg-black px-[3.5rem] py-[2.25rem] text-center font-geist text-[1.75rem] font-semibold capitalize leading-[1.35] tracking-[-0.035rem] text-white transition-opacity duration-200 hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
            >
              {text.ctaLabel}
            </button>
          </div>

          <DialogPrimitive.Close
            aria-label="Close"
            className="absolute right-[2rem] top-[2rem] rounded-[0.25rem] text-black transition-opacity hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-black"
          >
            <svg className="h-[3rem] w-[3rem]" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M13.0606 12L19.5 5.56058L18.4394 4.5L12 10.9394L5.56072 4.5L4.5 5.56058L10.9394 12L4.5 18.4394L5.56072 19.5L12 13.0606L18.4394 19.5L19.5 18.4394L13.0606 12Z"
                fill="currentColor"
              />
            </svg>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
